import {webanalyticsService} from 'services';
import {nbsp} from 'utils/typing/symbols';
import _ from 'lodash';
import groupBy from 'lodash.groupby';
_.noConflict();

function formatProfileName (profile) {
  return `№${profile.xid}${nbsp}${profile.name}`;
}

function formatGoalName (goal) {
  return `№${goal.xid}${nbsp}${goal.name}`;
}

function processProfile (profile, rootGetters) {
  const provider = rootGetters['channels/getProviderById'](profile.provider_id);

  return {
    ...profile,
    provider,
    icon: provider.icon,
    formattedName: formatProfileName(profile)
  };
}

function processGoal (goal) {
  return {
    ...goal,
    formattedName: formatGoalName(goal)
  };
}

function processProfiles (profiles, rootGetters) {
  return profiles.map(p => processProfile(p, rootGetters));
}

function processGoals (goals) {
  return goals.map(g => processGoal(g));
}

function mergeProfileAndGoals (profiles, goals) {
  const goalsProfileMap = groupBy(goals, (i) => i.profile_id);

  return profiles.map(profile => {
    profile.children = goalsProfileMap[profile.id] || [];
    return profile;
  });
}

function createState () {
  return {
    currentProfile: null,
    currentProfileGoals: [],

    accountProfiles: [],
    accountGoals: [],

    projectProfiles: [],
    projectGoals: []
  };
}

const webanalyticsStore = {
  namespaced: true,
  state: createState(),
  getters: {
    currentProfile: (state, getters, rootState, rootGetters) =>
      state.currentProfile ? processProfile(state.currentProfile, rootGetters) : {},
    currentProfileGoals: (state, getters, rootState, rootGetters) =>
      processGoals(state.currentProfileGoals, rootGetters),

    accountProfiles (state, getters, rootState, rootGetters) {
      return processProfiles(state.accountProfiles, rootGetters);
    },
    accountGoals (state, getters, rootState, rootGetters) {
      return processGoals(state.accountGoals, rootGetters);
    },
    accountProfilesAndGoals (state, getters) {
      return mergeProfileAndGoals(getters.accountProfiles, getters.accountGoals);
    },
    projectProfiles (state, getters, rootState, rootGetters) {
      return processProfiles(state.projectProfiles, rootGetters);
    },
    projectGoals (state) {
      return processGoals(state.projectGoals);
    },
    projectProfilesAndGoals (state, getters) {
      return mergeProfileAndGoals(getters.projectProfiles, getters.projectGoals);
    }
  },
  mutations: {
    SET_CURRENT_PROFILE (state, profile) {
      state.currentProfile = profile;
    },
    SET_CURRENT_PROFILE_GOALS (state, goals) {
      state.currentProfileGoals = goals;
    },

    TOGGLE_CURRENT_PROFILE_GOAL (state, {goalXid, state: goalState}) {
      const targetGoal = state.currentProfileGoals.find(i => i.xid === goalXid);
      if (!targetGoal) {
        console.error(`[TOGGLE_CURRENT_PROFILE_GOAL] Goal with xid ${goalXid} was not found`);
        return;
      }
      targetGoal.enabled = goalState;
    },

    SET_PROJECT_PROFILES (state, profiles) {
      state.projectProfiles = profiles;
    },
    SET_PROJECT_GOALS (state, goals) {
      state.projectGoals = goals;
    },

    TOGGLE_PROJECT_PROFILE_ENABLED_FROM_LIST (state, {profileId, enabled}) {
      const target = state.projectProfiles.find(p => p.id === profileId);
      target.enabled = enabled;
    },

    SET_ACCOUNT_PROFILES (state, profiles) {
      state.accountProfiles = profiles;
    },
    SET_ACCOUNT_GOALS (state, goals) {
      state.accountGoals = goals;
    }
  },
  actions: {
    async FETCH_CURRENT_PROFILE (context, {accountId, profileId}) {
      const profile = await webanalyticsService.getProfile({accountId, profileId});
      context.commit('SET_CURRENT_PROFILE', profile);
    },
    async FETCH_CURRENT_PROFILE_GOALS (context, {accountId, profileId}) {
      const goals = await webanalyticsService.getProfileGoals({accountId, profileId});
      context.commit('SET_CURRENT_PROFILE_GOALS', goals);
    },
    async UPDATE_CURRENT_PROFILE (context, updatedProfile) {
      context.commit('SET_CURRENT_PROFILE', updatedProfile);
    },
    async TOGGLE_CURRENT_PROFILE_GOAL (context, {goalXid, state}) {
      context.commit('TOGGLE_CURRENT_PROFILE_GOAL', {goalXid, state});
    },

    async TOGGLE_PROJECT_PROFILE_ENABLED_FROM_LIST (context, {accountId, profileId, enabled}) {
      context.commit('TOGGLE_PROJECT_PROFILE_ENABLED_FROM_LIST', {profileId, enabled});
      const profile = context.getters.projectProfiles.find(p => p.id === profileId);
      await webanalyticsService.saveProfileInfo(
        accountId,
        profileId,
        profile
      );
    },

    async FETCH_ACCOUNT_PROFILES (context, {accountId}) {
      const profiles = await webanalyticsService.getAccountProfilesList({
        accountId
      });
      context.commit('SET_ACCOUNT_PROFILES', profiles);
    },
    async FETCH_ACCOUNT_PROFILES_MANY (context, {accountIds}) {
      const profiles = await webanalyticsService.getAccountProfilesList({accountIds});
      context.commit('SET_ACCOUNT_PROFILES', profiles);
    },
    async FETCH_ACCOUNT_GOALS (context, {accountId}) {
      const goals = await webanalyticsService.getAccountGoalsList({accountId});
      context.commit('SET_ACCOUNT_GOALS', goals);
    },
    async FETCH_ACCOUNT_GOALS_MANY (context, {accountIds}) {
      const goals = await webanalyticsService.getAccountGoalsList({accountIds});
      context.commit('SET_ACCOUNT_GOALS', goals);
    },
    async FETCH_ACCOUNT_ANALYTICS (context, {accountId}) {
      await Promise.all([
        context.dispatch('FETCH_ACCOUNT_PROFILES', {accountId}),
        context.dispatch('FETCH_ACCOUNT_GOALS', {accountId})
      ]);
    },
    async FETCH_PROJECT_PROFILES (context, {accountId, projectId, fetchParams = {}}) {
      const profiles = await webanalyticsService.getProfilesList(accountId, projectId, fetchParams);
      context.commit('SET_PROJECT_PROFILES', profiles);
    },
    async FETCH_PROJECT_GOALS (context, {accountId, projectId}) {
      const goals = await webanalyticsService.getGoalsList(accountId, projectId);
      context.commit('SET_PROJECT_GOALS', goals);
    },
    async FETCH_PROJECT_ANALYTICS (context, {accountId, projectId}) {
      return Promise.all([
        context.dispatch('FETCH_PROJECT_PROFILES', {accountId, projectId}),
        context.dispatch('FETCH_PROJECT_GOALS', {accountId, projectId})
      ]);
    }
  }
};

export default webanalyticsStore;
